import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from './dialog';
import { Copy, Check, Download, FileText, Clock, Mic, Type, Calendar } from 'lucide-react';

/**
 * AudioTextModal — Modal premium para exibir o texto narrado de um áudio gerado.
 * DarkPlanner Design System: glassmorphism, #242424, #1C1C1C, #0DAEFF brand cyan.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - item: { fileName, voiceName, fullText, textLength, duration, createdAt }
 */
const AudioTextModal = ({ open, onOpenChange, item }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!item?.fullText) return;
    try {
      await navigator.clipboard.writeText(item.fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = item.fullText;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [item?.fullText]);

  const handleDownloadTxt = useCallback(() => {
    if (!item?.fullText) return;
    const blob = new Blob([item.fullText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${item.fileName || 'texto-audio'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [item?.fullText, item?.fileName]);

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (date) => {
    if (!date) return '—';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!item) return null;

  const charCount = item.textLength || item.fullText?.length || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className=""
        style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: '16px',
          padding: 0,
          maxWidth: '560px',
          width: 'calc(100% - 32px)',
          maxHeight: '85vh',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.03)',
          fontFamily: "var(--dp-font, 'Inter', sans-serif)",
        }}
      >
        <DialogTitle className="sr-only">Texto do áudio: {item.fileName}</DialogTitle>

        {/* ── Header ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px',
            background: 'rgba(13, 174, 255, 0.1)',
            border: '1px solid rgba(13, 174, 255, 0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <FileText size={18} style={{ color: '#0DAEFF' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '15px', fontWeight: 600, color: '#F5F5F5',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {item.fileName || 'Áudio'}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              Texto narrado
            </div>
          </div>
        </div>

        {/* ── Metadata Pills ── */}
        <div style={{
          padding: '12px 24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          borderBottom: '1px solid #2A2A2A',
        }}>
          {item.voiceName && (
            <MetaPill icon={<Mic size={12} />} label={item.voiceName} />
          )}
          {item.duration && (
            <MetaPill icon={<Clock size={12} />} label={formatDuration(item.duration)} />
          )}
          {charCount > 0 && (
            <MetaPill icon={<Type size={12} />} label={`${charCount.toLocaleString('pt-BR')} chars`} />
          )}
          {item.createdAt && (
            <MetaPill icon={<Calendar size={12} />} label={formatDate(item.createdAt)} />
          )}
        </div>

        {/* ── Text Content ── */}
        <div style={{
          padding: '16px 24px',
          maxHeight: 'calc(85vh - 240px)',
          overflowY: 'auto',
        }}
          className="ag-scrollbar"
        >
          <div style={{
            background: 'var(--dp-bg-secondary)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: '10px',
            padding: '16px',
          }}>
            <pre style={{
              margin: 0,
              fontFamily: "var(--dp-font, 'Inter', sans-serif)",
              fontSize: '13px',
              lineHeight: '1.7',
              color: '#D4D4D4',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '380px',
              overflowY: 'auto',
            }}
              className="ag-scrollbar"
            >
              {item.fullText || item.text || 'Texto não disponível'}
            </pre>
          </div>
        </div>

        {/* ── Footer Actions ── */}
        <div style={{
          padding: '14px 24px 18px',
          borderTop: '1px solid #2A2A2A',
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
        }}>
          <ActionButton
            onClick={handleCopy}
            icon={copied ? <Check size={14} /> : <Copy size={14} />}
            label={copied ? 'Copiado!' : 'Copiar texto'}
            active={copied}
          />
          <ActionButton
            onClick={handleDownloadTxt}
            icon={<Download size={14} />}
            label="Baixar .txt"
            primary
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Metadata Pill ── */
const MetaPill = ({ icon, label }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '4px 10px',
    borderRadius: '6px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid #2A2A2A',
    fontSize: '11px',
    color: '#A0A0A0',
    whiteSpace: 'nowrap',
  }}>
    <span style={{ color: '#666', display: 'flex' }}>{icon}</span>
    {label}
  </span>
);

/* ── Action Button ── */
const ActionButton = ({ onClick, icon, label, active, primary }) => {
  const [hovered, setHovered] = useState(false);

  const baseStyle = primary
    ? {
        background: hovered ? 'rgba(13, 174, 255, 0.15)' : 'rgba(13, 174, 255, 0.1)',
        border: '1px solid rgba(13, 174, 255, 0.25)',
        color: '#0DAEFF',
      }
    : active
    ? {
        background: 'rgba(52, 211, 153, 0.1)',
        border: '1px solid rgba(52, 211, 153, 0.25)',
        color: '#34D399',
      }
    : {
        background: hovered ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.04)',
        border: '1px solid #333',
        color: hovered ? '#F5F5F5' : '#A0A0A0',
      };

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...baseStyle,
        padding: '8px 16px',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'all 0.2s',
        fontFamily: "var(--dp-font, 'Inter', sans-serif)",
      }}
    >
      {icon}
      {label}
    </button>
  );
};

export default AudioTextModal;
